import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest, jsonBody } from '../../lib/api-client';
import type { AppNotification } from '../../lib/api-types';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: ['notifications', 'list'] as const,
  push: ['notifications', 'push-status'] as const,
};

export async function fetchNotifications(): Promise<AppNotification[]> {
  return (await apiRequest<{ notifications: AppNotification[] }>('/api/notifications'))
    .notifications;
}

export async function fetchPushStatus(): Promise<{ active: boolean }> {
  return apiRequest('/api/notifications/push-status');
}

export async function fetchPublicConfig(): Promise<{
  googleClientId: string;
  vapidPublicKey: string;
}> {
  return apiRequest('/api/config');
}

export async function markNotificationsRead(ids?: readonly string[]): Promise<number> {
  return (
    await apiRequest<{ updated: number }>('/api/notifications/mark-read', {
      body: jsonBody(ids ? { ids } : {}),
      method: 'POST',
    })
  ).updated;
}

export async function deleteNotification(id: string): Promise<void> {
  await apiRequest(`/api/notifications/${id}`, { method: 'DELETE' });
}

export async function clearNotifications(): Promise<number> {
  return (
    await apiRequest<{ deleted: number }>('/api/notifications/clear-all', { method: 'DELETE' })
  ).deleted;
}

export async function savePushSubscription(input: {
  active: boolean;
  endpoint: string;
  keys?: { auth: string; p256dh: string };
}): Promise<void> {
  await apiRequest('/api/notifications/subscribe', {
    body: jsonBody(input),
    method: 'POST',
  });
}

export async function sendTestPush(): Promise<{ delivered: number; failed: number }> {
  return apiRequest('/api/notifications/send-push', {
    body: jsonBody({
      message: 'Tus notificaciones están configuradas correctamente.',
      title: 'FinanceGo está listo',
      type: 'info',
    }),
    method: 'POST',
  });
}

export function useNotifications() {
  return useQuery({ queryFn: fetchNotifications, queryKey: notificationKeys.list });
}

export function usePushStatus() {
  return useQuery({ queryFn: fetchPushStatus, queryKey: notificationKeys.push });
}

export function usePublicConfig() {
  return useQuery({ queryFn: fetchPublicConfig, queryKey: ['public-config'] });
}

function useNotificationInvalidation() {
  const client = useQueryClient();
  return async () => client.invalidateQueries({ queryKey: notificationKeys.all });
}

export function useMarkNotificationsRead() {
  const invalidate = useNotificationInvalidation();
  return useMutation({ mutationFn: markNotificationsRead, onSuccess: invalidate });
}

export function useDeleteNotification() {
  const invalidate = useNotificationInvalidation();
  return useMutation({ mutationFn: deleteNotification, onSuccess: invalidate });
}

export function useClearNotifications() {
  const invalidate = useNotificationInvalidation();
  return useMutation({ mutationFn: clearNotifications, onSuccess: invalidate });
}

export function useSavePushSubscription() {
  const invalidate = useNotificationInvalidation();
  return useMutation({ mutationFn: savePushSubscription, onSuccess: invalidate });
}

export function useSendTestPush() {
  const invalidate = useNotificationInvalidation();
  return useMutation({ mutationFn: sendTestPush, onSuccess: invalidate });
}
